import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Container, Typography, List, ListItem, ListItemText, Box, Button, TextField, MenuItem, Stack, Switch, Chip, InputAdornment, Link } from '@mui/material';
import { ArrowBack as ArrowBackIcon, Save as SaveIcon, Close as CloseIcon, Delete as DeleteIcon, Add as AddIcon, Search as SearchIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import authoritativeDataSourcesService, { AuthoritativeDataSource } from '../../services/authoritativeDataSourcesService';
import { usePageTitle } from '../../hooks/usePageTitle';

const AuthoritativeDataSources: React.FC = () => {
  usePageTitle('Authoritative Data Sources');
  
  const navigate = useNavigate();
  const [items, setItems] = useState<AuthoritativeDataSource[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [editingId, setEditingId] = useState<string | 'new' | null>(null);
  const [form, setForm] = useState<Partial<AuthoritativeDataSource>>({});
  const [q, setQ] = useState<string>('');
  const firstFieldRef = useRef<HTMLInputElement | null>(null);

  const load = async (query?: string) => {
    setLoading(true);
    setError('');
    try {
      const data = await authoritativeDataSourcesService.list(query);
      setItems(Array.isArray(data) ? data : []);
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Failed to load data sources');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => { if (editingId && firstFieldRef.current) firstFieldRef.current.focus(); }, [editingId]);

  const startEdit = (it: AuthoritativeDataSource) => { setEditingId(it._id); setForm({ ...it }); };
  const startCreate = () => { setEditingId('new'); setForm({ name: '', sourceType: 'Other', order: (items?.length || 0) + 1, isActive: true }); };
  const cancelEdit = () => { setEditingId(null); setForm({}); };

  const save = async () => {
    try {
      if (editingId === 'new') {
        const created = await authoritativeDataSourcesService.create({
          name: form.name || '', sourceType: (form.sourceType as any) || 'Other',
          url: form.url, description: form.description, owner: form.owner,
          order: form.order, isActive: form.isActive,
        });
        setItems((prev) => [created, ...prev]);
      } else if (editingId) {
        const updated = await authoritativeDataSourcesService.update(editingId, form);
        setItems((prev) => prev.map((p) => (p._id === updated._id ? updated : p)));
      }
      cancelEdit();
    } catch (e: any) { setError(e?.response?.data?.error || 'Save failed'); }
  };

  const remove = async (id: string) => {
    try {
      await authoritativeDataSourcesService.remove(id);
      setItems((prev) => prev.filter((p) => p._id !== id));
      if (editingId === id) cancelEdit();
    } catch (e: any) { setError(e?.response?.data?.error || 'Delete failed'); }
  };

  const filtered = useMemo(() => {
    if (!Array.isArray(items)) return [];
    if (!q) return items;
    const s = q.toLowerCase();
    return items.filter((i) => [i.name, i.sourceType, i.description, i.owner].filter(Boolean).some((v) => String(v).toLowerCase().includes(s)));
  }, [items, q]);

  const renderForm = (isNew: boolean) => (
    <Stack spacing={1} sx={{ width: '100%' }}>
      <TextField inputRef={firstFieldRef} label="Name" fullWidth value={form.name || ''} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
      <Stack direction="row" spacing={1}>
        <TextField select label="Source Type" sx={{ width: 200 }} value={form.sourceType || 'Other'} onChange={(e) => setForm((f) => ({ ...f, sourceType: e.target.value as any }))}>
          {['Portal', 'API', 'Report', 'Database', 'Registry', 'Other'].map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
        </TextField>
        <TextField label="Owner" sx={{ flex: 1 }} value={form.owner || ''} onChange={(e) => setForm((f) => ({ ...f, owner: e.target.value }))} />
        <TextField label="Order" type="number" sx={{ width: 100 }} value={form.order ?? ''} onChange={(e) => setForm((f) => ({ ...f, order: Number(e.target.value) }))} />
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography component="span">Active</Typography>
          <Switch checked={!!form.isActive} onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))} />
        </Stack>
      </Stack>
      <TextField label="URL" fullWidth value={form.url || ''} onChange={(e) => setForm((f) => ({ ...f, url: e.target.value }))} />
      <TextField label="Description" multiline minRows={2} value={form.description || ''} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} />
      <Stack direction="row" spacing={1}>
        <Button onClick={save} startIcon={<SaveIcon />} variant="contained">Save</Button>
        <Button onClick={cancelEdit} startIcon={<CloseIcon />} color="inherit">Cancel</Button>
        {!isNew && <Button onClick={() => remove(form._id!)} startIcon={<DeleteIcon />} color="error">Delete</Button>}
      </Stack>
    </Stack>
  );

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 6 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Button onClick={() => navigate('/dhs')} startIcon={<ArrowBackIcon />} sx={{ mr: 2 }}>Back</Button>
        <Box sx={{ flex: 1 }} />
        <TextField size="small" placeholder="Search sources" value={q} onChange={(e) => setQ(e.target.value)} InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }} />
        <Button onClick={startCreate} startIcon={<AddIcon />} sx={{ ml: 2 }}>Add</Button>
      </Box>
      <Typography variant="h4" component="h1" sx={{ color: '#003366', fontWeight: 700, mb: 2 }}>Authoritative Data Sources</Typography>
      <Typography variant="body1" sx={{ mb: 3 }}>Open data, reports, and component portals/APIs for DHS data analysis.</Typography>
      {error && <Typography role="alert" sx={{ color: 'error.main', mb: 2 }}>{error}</Typography>}
      <List>
        {editingId === 'new' && <ListItem divider sx={{ bgcolor: 'background.default' }}>{renderForm(true)}</ListItem>}
        {filtered.map((it) => (
          <ListItem key={it._id} divider>
            {editingId === it._id ? renderForm(false) : (
              <Box role="button" tabIndex={0} onClick={() => startEdit(it)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); startEdit(it); }}} style={{ width: '100%', outline: 'none' }}>
                <Stack spacing={0.5}>
                  <ListItemText primaryTypographyProps={{ sx: { fontWeight: 700 } }} primary={it.name} />
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip size="small" color="primary" label={it.sourceType} />
                    {it.owner && <Chip size="small" label={it.owner} />}
                    <Chip size="small" label={it.isActive ? 'Active' : 'Inactive'} color={it.isActive ? 'success' : 'default'} />
                  </Stack>
                  {it.url && <Link href={it.url} target="_blank" rel="noopener" onClick={(e) => e.stopPropagation()}><Typography variant="body2">{it.url}</Typography></Link>}
                  {it.description && <Typography variant="body2" sx={{ color: 'text.secondary' }}>{it.description}</Typography>}
                </Stack>
              </Box>
            )}
          </ListItem>
        ))}
        {loading && <ListItem><ListItemText primary="Loading..." /></ListItem>}
        {!loading && filtered.length === 0 && <ListItem><ListItemText primary="No sources found" /></ListItem>}
      </List>
    </Container>
  );
};

export default AuthoritativeDataSources;
